import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { MediaApiService } from "../../../services/media-controller/media-api.service";
import { MediaFile } from "../../../models/media-file";

/**
 * Modal picker for selecting a single MediaFile from the library. Used by the bot
 * action editor when a system bot is configured as "Download file".
 *
 * <p>Day-1 scope: flat list, name-prefix filter, single-select. The architect's plan
 * defers fuzzy search and grouping until the library exceeds 30 files in any
 * deployment.
 *
 * <p>Caller binds {@code [visible]="true"} to open the picker, listens to
 * {@code (selected)} for the chosen file, and {@code (cancelled)} for dismiss.
 */
@Component({
  selector: "media-picker",
  templateUrl: "./media-picker.component.html",
})
export class MediaPickerComponent implements OnInit, OnChanges {

  @Input() public visible = false;
  /** Pre-selected media file id (e.g. when re-opening on an already-configured action). */
  @Input() public selectedMediaFileId: string;

  @Output() public selected = new EventEmitter<MediaFile>();

  public files: MediaFile[] = [];
  public loading = false;
  public filter = "";

  constructor(
      private mediaApi: MediaApiService,
      private toastr: ToastrService,
  ) {
  }

  public ngOnInit(): void {
    if (this.visible) {
      this.loadFiles();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Lazy-load: only fetch when the picker is actually shown. Re-fetch every time it
    // re-opens so the admin always sees a fresh list (e.g. after uploading a new file
    // in another tab).
    if (changes.visible && this.visible && !changes.visible.previousValue) {
      this.loadFiles();
    }
  }

  public filtered(): MediaFile[] {
    const needle = (this.filter || "").trim().toLowerCase();
    if (!needle) {
      return this.files;
    }
    return this.files.filter((f) => f.name?.toLowerCase().includes(needle));
  }

  public choose(file: MediaFile): void {
    this.selected.emit(file);
  }

  public formatSize(bytes: number): string {
    if (bytes == null) {return "-";}
    if (bytes < 1024) {return bytes + " B";}
    if (bytes < 1024 * 1024) {return (bytes / 1024).toFixed(1) + " KB";}
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  private loadFiles(): void {
    this.loading = true;
    this.mediaApi.list().subscribe({
      next: (files) => {
        this.files = files;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error("Failed to load media library: " + (err?.message || "unknown error"));
      },
    });
  }
}
